import { SolutionFormDateValue } from "../form/types";

const minimumTimestampDate = new Date(1970, 1, 1);
const dateTimeLocale = "en-GB";
const utcTimeZone = "UTC";

const isValidNumber = (value: number | undefined): value is number =>
  Number.isFinite(value);

export const hasTimestamp = (
  timestamp: number | null | undefined,
): timestamp is number => timestamp !== null && timestamp !== undefined;

export const isValidTimestamp = (
  timestamp: number | null | undefined,
): timestamp is number => hasTimestamp(timestamp) && Number.isFinite(timestamp);

const isMissingTimestampValue = (
  value: string | number | null | undefined,
): value is null | undefined | "" =>
  value === null || value === undefined || value === "";

export const isValidSolutionTimestamp = (
  timestamp: SolutionFormDateValue | undefined,
): boolean => {
  if (isMissingTimestampValue(timestamp)) return true;

  const parsedTimestamp = Number(timestamp);
  return (
    Number.isFinite(parsedTimestamp) &&
    parsedTimestamp > minimumTimestampDate.getTime()
  );
};

export const transformTimestampValidity = (
  _: boolean,
  originalValue: SolutionFormDateValue | undefined,
): boolean => !isValidSolutionTimestamp(originalValue);

export const formatNumber = (
  value: number | undefined,
  maximumFractionDigits = 1,
): string | null => {
  if (!isValidNumber(value)) return null;

  return new Intl.NumberFormat(dateTimeLocale, {
    maximumFractionDigits,
  }).format(value);
};

export const normaliseTimestamp = (
  value: string | number | undefined | null,
): number | null => {
  if (isMissingTimestampValue(value)) return null;

  const timestamp = typeof value === "number" ? value : Date.parse(value);
  if (!Number.isFinite(timestamp)) return null;

  return timestamp > 0 && timestamp < 100000000000
    ? timestamp * 1000
    : timestamp;
};

export const parseTimestamp = (
  timestamp: number | string | null | undefined,
): number | null => {
  if (isMissingTimestampValue(timestamp)) return null;

  const parsedTimestamp = Number.parseInt(timestamp.toString(), 10);
  return Number.isNaN(parsedTimestamp) ? null : parsedTimestamp;
};

const getDate = (value: string | number | undefined): Date | null => {
  const timestamp = normaliseTimestamp(value);
  if (timestamp === null) return null;

  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateValue = (
  value: string | number | undefined,
  options: Intl.DateTimeFormatOptions,
): string | null => {
  const date = getDate(value);
  if (!date) return null;

  return new Intl.DateTimeFormat(dateTimeLocale, {
    ...options,
    timeZone: utcTimeZone,
  }).format(date);
};

export const formatDate = (value: string | number | undefined): string | null =>
  formatDateValue(value, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

export const formatDateTime = (
  value: string | number | undefined,
): string | null =>
  formatDateValue(value, {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

export const formatShortDate = (
  value: string | number | undefined,
): string | null =>
  formatDateValue(value, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

export const formatShortDateTime = (
  value: string | number | undefined,
): string | null => {
  const date = getDate(value);
  if (!date) return null;

  const formattedDate = new Intl.DateTimeFormat(dateTimeLocale, {
    day: "numeric",
    month: "numeric",
    year: "numeric",
    timeZone: utcTimeZone,
  }).format(date);

  const formattedTime = new Intl.DateTimeFormat(dateTimeLocale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: utcTimeZone,
  }).format(date);

  return `${formattedDate} at ${formattedTime}`;
};
